import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// mui components
import {
    Paper,
    Divider,
    styled,
    Grid,
    Box,
    Button,
    Modal,
    Typography,
    TextField,
    Card,
    CardHeader,
    CardContent,
    Stack
} from "@mui/material";
import Autocomplete from '@mui/material/Autocomplete';
import MenuItem from '@mui/material/MenuItem';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // Added for visual cue

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '94%', sm: 700, md: 800 },
    maxHeight: '90vh',
    overflowY: 'auto',
    outline: 'none',
    border: 'none',
    boxSizing: 'border-box'
};

// Reusable standard layout styling dictionary configuring premium outlined enterprise inputs
const enterpriseLineItemInputStyles = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid #E2E8F0',
        '& fieldset': { border: 'none' },
        '&:hover': {
            borderColor: '#CBD5E1',
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
        },
        '&.Mui-focused': {
            borderColor: '#1976d2', // --- CORPORATE IDENTITY ACCENT BRAND BLUE ---
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
        }
    },
    '& .MuiInputLabel-outlined': {
        color: '#64748B',
        fontWeight: 500,
        transform: 'translate(14px, 10px) scale(1)',
        '&.Mui-focused, &.MuiInputLabel-shrink': {
            transform: 'translate(14px, -8px) scale(0.75)',
            color: '#1976d2',
            fontWeight: 600
        }
    },
    '& input, & .MuiSelect-select': {
        padding: '10px 14px !important',
        fontSize: '13.5px',
        color: '#1E293B',
        fontWeight: 500
    },
    '& .MuiFormHelperText-root': {
        mx: 0.5,
        mt: 0.75,
        fontWeight: 500
    }
};

const AddLineItem = ({ addLineItemVisible, onAddLineItemModalClose, formDataHandler,dispatchHandler,dispatchCountryCode, onAddLineItemClick, addLineItemData, editLineItemId, errors,handleBlur,handleHsCodeBlur,skuOnly,isShipmentCreationFlow }) => {
    const country_code = useSelector(state => state.countrystate.country);
    const filteredCountryData = country_code.data.filter(({ code }) => code );
    const onChangeCountry = id => {
        dispatchCountryCode(id)
    }
    const getSelectedCountry = (country_code) => {
        let filteredValue = { code: "", name: "" };
        filteredCountryData.map((key) => {
          if (key.code == country_code) {
            filteredValue = key;
          }
        });
         return filteredValue;
      };
    return (
        <>
            <Modal
                open={addLineItemVisible}
                onClose={onAddLineItemModalClose}
                closeAfterTransition
            >
                <Box sx={modalStyle}>
                    <Card 
                        sx={{ 
                            backgroundColor: '#FFFFFF',
                            borderRadius: '16px',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                            border: '1px solid #E2E8F0',
                            overflow: 'hidden'
                        }} 
                        className="sc--cardhead--details"
                    >
                        <CardHeader 
                            title={
                                <Typography variant="h6" component="h2" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '16px' }}>
                                    {(editLineItemId != "") ? "Update Line Item" : "New Line Item"}
                                </Typography>
                            } 
                            sx={{ p: 2.5, backgroundColor: '#FFFFFF' }}
                        />
                        <Divider sx={{ borderColor: '#E2E8F0' }} />
                        <CardContent sx={{ p: 4, boxSizing: 'border-box' }}>
                            <Grid container spacing={3}>
                                
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ minHeight: '40px' }}>
                                        <Autocomplete
                                            options={skuOnly}
                                            value={addLineItemData.sku || ''}
                                            freeSolo
                                            size="small"
                                            // onChange triggers when an item is SELECTED from the list
                                            onChange={(e, newValue) => {
                                                const syntheticEvent = {
                                                    target: {
                                                        name: 'sku',
                                                        value: newValue || ''
                                                    }
                                                };
                                                formDataHandler(syntheticEvent);
                                                dispatchHandler(syntheticEvent); // Auto-fill details on selection
                                            }}
                                            // onInputChange handles both manual TYPING and PASTE
                                            onInputChange={(e, newInputValue, reason) => {
                                                // reason === 'input' covers typing and pasting
                                                if (reason === 'input' || reason === 'clear') {
                                                    const syntheticEvent = {
                                                        target: {
                                                            name: 'sku',
                                                            value: newInputValue || ''
                                                        }
                                                    };
                                                    formDataHandler(syntheticEvent);
                                                    
                                                    // Optional: If you want to trigger the lookup immediately on paste:
                                                    if (newInputValue && newInputValue.length >= 1) {
                                                        dispatchHandler(syntheticEvent);
                                                    }
                                                }
                                            }}
                                            sx={{
                                                width: '100%',
                                                '& .MuiAutocomplete-inputRoot': enterpriseLineItemInputStyles['& .MuiOutlinedInput-root'],
                                                '& .MuiInputLabel-root': enterpriseLineItemInputStyles['& .MuiInputLabel-outlined'],
                                                '& input': enterpriseLineItemInputStyles['& input']
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    id="standard-basic"
                                                    label="SKU*"
                                                    variant="outlined"
                                                    name="sku"
                                                    error={!!errors.sku}
                                                    helperText={errors.sku}
                                                    sx={{ '& .MuiFormHelperText-root': enterpriseLineItemInputStyles['& .MuiFormHelperText-root'] }}
                                                />
                                            )}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ minHeight: '40px' }}>
                                        <TextField id="standard-basic"
                                            label="Product Id*"
                                            variant="outlined"
                                            size="small"
                                            name="product_id"
                                            value={addLineItemData.product_id}
                                            onChange={formDataHandler}
                                            error={!!errors.product_id}
                                            helperText={errors.product_id}
                                            sx={enterpriseLineItemInputStyles}
                                        />
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ minHeight: '40px' }}>
                                        <TextField id="standard-basic"
                                            label="Product Description*"
                                            variant="outlined"
                                            size="small"
                                            name="product_desc"
                                            onBlur={handleBlur}
                                            value={addLineItemData.product_desc}
                                            onChange={formDataHandler}
                                            error={!!errors.product_desc}
                                            helperText={errors.product_desc}
                                            sx={enterpriseLineItemInputStyles}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ minHeight: '40px' }}>
                                        <TextField id="standard-basic"
                                            label="Product Url"
                                            variant="outlined"
                                            size="small"
                                            name="product_url"
                                            value={addLineItemData.product_url}
                                            onChange={formDataHandler}
                                            error={!!errors.product_url}
                                            helperText={errors.product_url}
                                            sx={enterpriseLineItemInputStyles}
                                        />
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ minHeight: '40px' }}>
                                        <TextField id="standard-basic"
                                            label="Currency*"
                                            variant="outlined"
                                            size="small"
                                            name="currency"
                                            value={addLineItemData.currency}
                                            onChange={formDataHandler}
                                            error={!!errors.currency}
                                            helperText={errors.currency}
                                            sx={enterpriseLineItemInputStyles}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ minHeight: '40px' }}>
                                        <TextField id="standard-basic"
                                            label="Unit Price*"
                                            variant="outlined"
                                            size="small"
                                            name="product_price"
                                            value={addLineItemData.product_price}
                                            onBlur={handleBlur}
                                            onChange={formDataHandler}
                                            error={!!errors.product_price}
                                            helperText={errors.product_price}
                                            sx={enterpriseLineItemInputStyles}
                                        />
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ minHeight: '40px' }}>
                                        <TextField id="standard-basic"
                                            label="Quantity*"
                                            variant="outlined"
                                            size="small"
                                            name="quantity"
                                            value={addLineItemData.quantity}
                                            onChange={formDataHandler}
                                            error={!!errors.quantity}
                                            helperText={errors.quantity}
                                            sx={enterpriseLineItemInputStyles}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ minHeight: '40px' }}>
                                        <Autocomplete
                                            className="help-text-error"
                                            disablePortal
                                            size="small"
                                            name="origin_country_code*"
                                            id="country-select-demo"
                                            options={filteredCountryData}
                                            autoHighlight
                                            loading={country_code && country_code.loading}
                                            getOptionLabel={(option) => option.name || ""}
                                            value={getSelectedCountry(addLineItemData && addLineItemData.origin_country_code)}
                                            sx={{
                                                width: '100%',
                                                '& .MuiAutocomplete-inputRoot': enterpriseLineItemInputStyles['& .MuiOutlinedInput-root'],
                                                '& .MuiInputLabel-root': enterpriseLineItemInputStyles['& .MuiInputLabel-outlined'],
                                                '& input': enterpriseLineItemInputStyles['& input']
                                            }}
                                            renderOption={(props, option) => (
                                            <MenuItem {...props} value={option.code} key={option.code} sx={{ fontSize: '13.5px', fontWeight: 500 }}>
                                                {option.icon} {option.name}
                                            </MenuItem>
                                            )}
                                            onChange={(event, value) => {
                                            if (value !== null) {
                                                onChangeCountry(value.code);
                                            } else {
                                                onChangeCountry("");
                                            }
                                            }}
                                            renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Country of Origin*"
                                                variant="outlined"
                                                error={!!errors.origin_country_code}
                                                helperText={errors.origin_country_code}
                                                sx={{ '& .MuiFormHelperText-root': enterpriseLineItemInputStyles['& .MuiFormHelperText-root'] }}
                                            />
                                            )}
                                        />
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ minHeight: '40px' }}>
                                        <TextField 
                                            label="Hs Code*" 
                                            variant="outlined" 
                                            size="small"
                                            name="hs_tariff_code" 
                                            value={addLineItemData.hs_tariff_code || ''} 
                                            onChange={formDataHandler} 
                                            onBlur={handleHsCodeBlur} 
                                            error={!!errors.hs_tariff_code}
                                            // --- DYNAMIC DIGIT COUNTER ---
                                            helperText={
                                                <Typography 
                                                    variant="caption" 
                                                    component="span" 
                                                    sx={{ 
                                                        fontWeight: 600,
                                                        color: addLineItemData.hs_tariff_code?.replace(/\./g, '').length === 10 
                                                            ? '#2E7D32' 
                                                            : (errors.hs_tariff_code ? '#D32F2F' : '#64748B') 
                                                    }}
                                                >
                                                    {errors.hs_tariff_code ? errors.hs_tariff_code : 
                                                    `Digits: ${addLineItemData.hs_tariff_code?.replace(/\./g, '').length || 0} / 10 required`}
                                                </Typography>
                                            }
                                            sx={enterpriseLineItemInputStyles}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ minHeight: '40px' }}>
                                        <TextField 
                                            label="Hs Country Code" 
                                            variant="outlined" 
                                            size="small"
                                            name="hs_tariff_code_country" 
                                            value={addLineItemData.hs_tariff_code_country || ''} 
                                            onChange={formDataHandler} 
                                            sx={enterpriseLineItemInputStyles}
                                        />
                                    </Box>
                                </Grid>

                                {isShipmentCreationFlow && (
                                    <Grid item xs={12}>
                                        <Box 
                                            sx={{ 
                                                bgcolor: '#FFFDE7', // Light Yellow Background
                                                p: 1.5, 
                                                borderRadius: '12px', 
                                                border: '1px solid #FFF59D',
                                                borderLeft: '4px solid #FBC02D', // Darker Yellow Border
                                                boxShadow: '0px 1px 3px rgba(0,0,0,0.02)'
                                            }}
                                        >
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <InfoOutlinedIcon sx={{ color: '#F57F17', fontSize: 20 }} />
                                                <Typography variant="caption" sx={{ color: '#5D4037', fontWeight: 600 }}>
                                                    Note: Changes apply to this order only. Inventory and other orders will not be updated.
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pt: 2, borderTop: '1px solid #E2E8F0', mt: 1 }}>
                                        <Button
                                            variant="contained"
                                            onClick={onAddLineItemClick}
                                            sx={{
                                                textTransform: 'none',
                                                borderRadius: '10px',
                                                fontWeight: 700,
                                                fontSize: '13.5px',
                                                height: '38px',
                                                px: 4,
                                                boxShadow: 'none',
                                                backgroundColor: '#1976d2',
                                                color: '#FFFFFF',
                                                '&:hover': { backgroundColor: '#115293', boxShadow: 'none' }
                                            }}
                                        >
                                            {(editLineItemId != "") ? "Update Item" : "Add Item"}
                                        </Button>
                                    </Box>
                                </Grid>

                            </Grid>
                        </CardContent>
                    </Card>
                </Box>
            </Modal>
        </>
    )
}

export default AddLineItem;